import { formatDistanceToNow } from 'date-fns';
import { useRouter } from 'expo-router';
import {
  Briefcase,
  Building2,
  Clock,
  FileText,
  GripVertical,
  Mail,
  PhoneCall,
  User,
} from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Platform, Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import { BotIcon } from '../../../components/icons/commandDeckIcons';
import { Badge, Text } from '../../../components/primitives';
import { BentoSurface } from '../../../components/primitives/BentoSurface';
import { useTranslation } from '../../../contexts/TranslationContext';
import { DATE_LOCALES } from '../../../lib/dateLocales';
import { colors } from '../../../theme/colors';
import { cardSurface } from '../../../theme/surfaces';
import { tokens } from '../../../theme/tokens';
import type { Lead } from '../../../types/leads';
import { getLeadCardContactName, getLeadCardDescription } from '../utils/leadCardFields';
import { getLeadDisplayName } from '../utils/leadDisplayName';
import { useDragContext } from './DragContext';
import { LeadSourceBadges } from './LeadSourceBadges';

interface LeadCardProps {
  lead: Lead;
  stageId: string;
  stageColor?: string;
  stageName?: string;
  dragEnabled?: boolean;
  onPress?: (lead: Lead) => void;
}

function getDisplayScore(lead: Lead): number | undefined {
  const qualificationScore = lead.customFields?.qualificationScore as number | undefined;
  if (qualificationScore !== undefined) return qualificationScore;
  const analysisResult = lead.customFields?.analysisResult as { score?: number } | undefined;
  return analysisResult?.score;
}

function getScoreColors(score: number) {
  if (score >= 70) return { bg: colors.status.success.bg, text: colors.status.success.main };
  if (score >= 40) return { bg: colors.status.warning.bg, text: colors.status.warning.main };
  return { bg: colors.status.error.bg, text: colors.status.error.main };
}

function ScoreBadge({ score }: Readonly<{ score: number }>) {
  const scoreColors = getScoreColors(score);
  return (
    <View style={[styles.scorePill, { backgroundColor: scoreColors.bg }]}>
      <Text style={[styles.scoreText, { color: scoreColors.text }]}>{score}</Text>
    </View>
  );
}

function InfoLine({
  icon: Icon,
  text,
  testID,
}: Readonly<{ icon: typeof Building2; text: string; testID?: string }>) {
  return (
    <View testID={testID} style={styles.infoLine}>
      <Icon size={12} color={colors.text.tertiary} strokeWidth={1.7} />
      <Text style={styles.infoText} numberOfLines={1} ellipsizeMode="tail">
        {text}
      </Text>
    </View>
  );
}

export function LeadCard({
  lead,
  stageId,
  stageColor,
  stageName,
  dragEnabled = true,
  onPress,
}: Readonly<LeadCardProps>) {
  const { t, language } = useTranslation();
  const router = useRouter();
  const isContactedStage = stageName?.toLowerCase() === 'contacted';
  const [isHovered, setIsHovered] = useState(false);
  const { draggingLead, startDrag, endDrag } = useDragContext();
  const isDragging = draggingLead?.id === lead.id;
  const dragRef = useRef<View>(null);

  const scaleAnim = useRef(new Animated.Value(1)).current;
  const opacityAnim = useRef(new Animated.Value(1)).current;

  const startDragRef = useRef(startDrag);
  startDragRef.current = startDrag;
  const endDragRef = useRef(endDrag);
  endDragRef.current = endDrag;
  const leadRef = useRef(lead);
  leadRef.current = lead;
  const stageIdRef = useRef(stageId);
  stageIdRef.current = stageId;

  useEffect(() => {
    if (!dragEnabled || Platform.OS !== 'web' || !dragRef.current) return;

    const node = dragRef.current as unknown as HTMLElement;
    if (!node?.setAttribute) return;

    node.setAttribute('draggable', 'true');
    node.style.cursor = 'grab';

    const handleDragStart = (e: DragEvent) => {
      e.dataTransfer?.setData('text/plain', leadRef.current.id);
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
      }
      node.style.cursor = 'grabbing';
      node.style.opacity = '0.5';
      startDragRef.current(leadRef.current, stageIdRef.current);
    };

    const handleDragEnd = () => {
      node.style.cursor = 'grab';
      node.style.opacity = '1';
      endDragRef.current();
    };

    node.addEventListener('dragstart', handleDragStart);
    node.addEventListener('dragend', handleDragEnd);

    return () => {
      node.removeEventListener('dragstart', handleDragStart);
      node.removeEventListener('dragend', handleDragEnd);
    };
  }, [dragEnabled]);

  const handleLongPress = useCallback(() => {
    if (Platform.OS === 'web') return;
    startDrag(lead, stageId);
    Animated.parallel([
      Animated.spring(scaleAnim, { toValue: 1.05, useNativeDriver: true }),
      Animated.timing(opacityAnim, { toValue: 0.8, duration: 150, useNativeDriver: true }),
    ]).start();
  }, [lead, stageId, startDrag, scaleAnim, opacityAnim]);

  const handlePressOut = useCallback(() => {
    if (Platform.OS === 'web') return;
    if (isDragging) {
      endDrag();
      Animated.parallel([
        Animated.spring(scaleAnim, { toValue: 1, useNativeDriver: true }),
        Animated.timing(opacityAnim, { toValue: 1, duration: 150, useNativeDriver: true }),
      ]).start();
    }
  }, [isDragging, endDrag, scaleAnim, opacityAnim]);

  const webHoverStyle = Platform.select<ViewStyle>({
    web: {
      transition: 'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
      cursor: 'pointer',
    } as ViewStyle,
    default: {},
  });

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    },
    default: {},
  });

  const timeAgo = formatDistanceToNow(new Date(lead.createdAt), {
    addSuffix: true,
    locale: DATE_LOCALES[language],
  });
  const displayScore = getDisplayScore(lead);
  const isActive = isHovered || isDragging;
  const title = getLeadDisplayName(lead) || t('common.fallbacks.unknownLead');
  const companyLine = getLeadCardContactName(lead);
  const description = getLeadCardDescription(lead);

  const cardElevation =
    Platform.OS === 'web' && isActive ? tokens.shadow?.sm : tokens.shadow?.subtle;

  const cardContent = (
    <View
      testID={`kanban_lead_${lead.id}_surface`}
      style={[
        styles.card,
        cardElevation as ViewStyle,
        isActive && styles.cardActive,
        isDragging && styles.cardDragging,
        webHoverStyle,
      ]}
      {...hoverProps}
    >
      <BentoSurface radius={tokens.radius.lg} />
      <View style={styles.titleRow}>
        {dragEnabled && Platform.OS === 'web' && (
          <View style={styles.grip}>
            <GripVertical size={13} color={colors.text.tertiary} strokeWidth={1.5} />
          </View>
        )}
        <Text testID="lead_card_title" style={styles.title} numberOfLines={1} ellipsizeMode="tail">
          {title}
        </Text>
        {displayScore !== undefined && <ScoreBadge score={displayScore} />}
      </View>

      <View style={styles.infoStack}>
        {companyLine && <InfoLine testID="lead_card_contact" icon={Building2} text={companyLine} />}
        {lead.jobTitle && <InfoLine icon={Briefcase} text={lead.jobTitle} />}
        {lead.email && <InfoLine icon={Mail} text={lead.email} />}
      </View>

      {description && (
        <View testID="lead_card_description" style={styles.descriptionRow}>
          <FileText size={12} color={colors.text.tertiary} strokeWidth={1.7} />
          <Text style={styles.infoText} numberOfLines={2} ellipsizeMode="tail">
            {description}
          </Text>
        </View>
      )}

      {lead.tags.length > 0 && (
        <View style={styles.tagsRow}>
          {lead.tags.slice(0, 3).map((tag) => (
            <View key={tag} style={styles.tagChip}>
              <BotIcon size={11} color={stageColor ?? colors.primary[400]} strokeWidth={2} />
              <Text style={styles.tagText} numberOfLines={1}>
                {tag}
              </Text>
            </View>
          ))}
          {lead.tags.length > 3 && <Text style={styles.tagOverflow}>+{lead.tags.length - 3}</Text>}
        </View>
      )}

      <View style={styles.footer}>
        <View style={styles.footerLeft}>
          <LeadSourceBadges lead={lead} showLabel={false} />
          {lead.qualificationStatus === 'pending_validation' && (
            <Badge variant="subtle" color="amber" size="sm">
              {t('leads.detail.qualification.pending')}
            </Badge>
          )}
          {lead.qualificationStatus === 'not_qualified_title' && (
            <Badge variant="subtle" color="red" size="sm">
              {t('leads.detail.qualification.notQualified')}
            </Badge>
          )}
        </View>

        <View style={styles.footerRight}>
          {lead.assignedUser && (
            <View style={styles.assignee}>
              <View style={styles.assigneeAvatar}>
                <User size={9} color={colors.primary[500]} strokeWidth={2} />
              </View>
              <Text style={styles.assigneeName} numberOfLines={1}>
                {lead.assignedUser.fullName.split(' ')[0]}
              </Text>
            </View>
          )}
          {isContactedStage && lead.phone && (
            <Pressable
              onPress={(e) => {
                e?.stopPropagation?.();
                router.push(`/lead/${lead.id}`);
              }}
              accessibilityRole="button"
              accessibilityLabel="AI voice call"
              style={styles.callButton}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <PhoneCall size={12} color={colors.primary[400]} strokeWidth={2} />
            </Pressable>
          )}
          <View style={styles.timestamp}>
            <Clock size={10} color={colors.text.tertiary} strokeWidth={1.5} />
            <Text style={styles.timestampText} numberOfLines={1}>
              {timeAgo}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );

  if (Platform.OS === 'web') {
    if (dragEnabled) {
      return (
        <Pressable
          testID={`kanban_lead_${lead.id}_card`}
          onPress={() => onPress?.(lead)}
          style={styles.pressable}
        >
          <View ref={dragRef} style={styles.dragRefHost}>
            {cardContent}
          </View>
        </Pressable>
      );
    }
    return (
      <Pressable
        testID={`kanban_lead_${lead.id}_card`}
        onPress={() => onPress?.(lead)}
        style={styles.pressable}
      >
        {cardContent}
      </Pressable>
    );
  }

  return (
    <Pressable
      testID={`kanban_lead_${lead.id}_card`}
      onPress={() => onPress?.(lead)}
      onLongPress={dragEnabled ? handleLongPress : undefined}
      onPressOut={dragEnabled ? handlePressOut : undefined}
      delayLongPress={300}
      style={styles.pressable}
    >
      {dragEnabled ? (
        <Animated.View
          style={[styles.dragRefHost, { transform: [{ scale: scaleAnim }], opacity: opacityAnim }]}
        >
          {cardContent}
        </Animated.View>
      ) : (
        cardContent
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  // iOS needs alignSelf:'stretch' so the card fills its parent ScrollView/column
  // width; web needs width:'100%' because Pressable doesn't always honor
  // alignSelf as a flex item. Mirrors the LeadListItem pressable pattern.
  pressable: {
    alignSelf: 'stretch',
    ...(Platform.OS === 'web' ? ({ width: '100%' } as ViewStyle) : {}),
  },
  dragRefHost: {
    width: '100%',
  },
  card: {
    ...cardSurface(),
    width: '100%',
    maxWidth: '100%',
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing.md,
    gap: tokens.spacing.xs,
    overflow: 'hidden',
  },
  cardActive: {
    borderColor: colors.primary.border,
    backgroundColor: colors.surface.rowHover,
  },
  cardDragging: {
    borderColor: colors.primary[500],
    backgroundColor: colors.primary.muted,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  grip: {
    opacity: 0.5,
  },
  title: {
    flex: 1,
    minWidth: 0,
    color: colors.text.primary,
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 18,
  },
  scorePill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    flexShrink: 0,
  },
  scoreText: {
    fontWeight: '700',
    fontSize: 10,
    lineHeight: 12,
  },
  infoStack: {
    gap: 2,
  },
  infoLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  descriptionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  infoText: {
    flex: 1,
    minWidth: 0,
    color: colors.text.secondary,
    fontSize: 12,
    lineHeight: 16,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
    minHeight: 36,
    gap: tokens.spacing.xs,
  },
  footerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    flexWrap: 'wrap',
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  footerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    flexShrink: 0,
  },
  assignee: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  assigneeAvatar: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.primary.muted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  assigneeName: {
    color: colors.text.tertiary,
    fontSize: 10,
    fontWeight: '500',
    maxWidth: 60,
  },
  timestamp: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  timestampText: {
    color: colors.text.tertiary,
    fontSize: 10,
    maxWidth: 90,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 2,
  },
  tagChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.background.secondary,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tagText: {
    color: colors.text.tertiary,
    fontSize: 10,
    fontWeight: '600',
  },
  tagOverflow: {
    color: colors.text.tertiary,
    fontSize: 10,
    fontWeight: '500',
    alignSelf: 'center',
  },
  callButton: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: colors.primary.muted,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
